import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import InstallGuide from './pages/InstallGuide';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Invoices from './pages/Invoices';
import CreateInvoice from './pages/CreateInvoice';
import ViewInvoice from './pages/ViewInvoice';
import EditInvoice from './pages/EditInvoice';
import UserManagement from './pages/UserManagement';
import NotFound from './pages/NotFound';
import Receipts from './pages/Receipts';
import ViewReceipt from './pages/ViewReceipt';
import CreateReceipt from './pages/CreateReceipt';
import Clients from './pages/Clients';
import Profile from './pages/Profile';

// Components
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import InstallPWA from './components/InstallPWA';

function App() {
  const [user, setUser] = useState(null);
  const [authIsReady, setAuthIsReady] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        
        try {
          // Get user role from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || 'user');
          } else {
            setUserRole('user');
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          setUserRole('user');
        }
      } else {
        setUser(null);
        setUserRole(null);
      }
      setAuthIsReady(true);
    });

    return unsubscribe;
  }, []);

  // Show offline notification
  const OfflineNotification = () => (
    !isOnline && (
      <div className="offline-notification">
        You are currently offline. Some features may be limited.
      </div>
    )
  );

  return (
    <div className="App">
      {authIsReady && (
        <BrowserRouter>
          <OfflineNotification />
          {user && <Navbar key={userRole} userRole={userRole} />}
          <InstallPWA />
          <Routes>
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
            <Route path="/signup" element={!user ? <Signup /> : <Navigate to="/" />} />
            
            <Route path="/" element={
              <ProtectedRoute user={user}>
                <Dashboard userRole={userRole} />
              </ProtectedRoute>
            } />
            
            <Route path="/invoices" element={
              <ProtectedRoute user={user}>
                <Invoices userRole={userRole} />
              </ProtectedRoute>
            } />
            
            <Route path="/create-invoice" element={
              <ProtectedRoute user={user}>
                <CreateInvoice userRole={userRole} />
              </ProtectedRoute>
            } />
            
            <Route path="/invoice/:id" element={
              <ProtectedRoute user={user}>
                <ViewInvoice userRole={userRole} />
              </ProtectedRoute>
            } />
            
            <Route path="/edit-invoice/:id" element={
              <ProtectedRoute user={user}>
                <EditInvoice userRole={userRole} />
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <AdminRoute user={user}>
                <UserManagement />
              </AdminRoute>
            } />
            
            <Route path="/clients" element={
              <ProtectedRoute user={user}>
                <Clients userRole={userRole} />
              </ProtectedRoute>
            } />
            
            <Route path="/install-guide" element={<InstallGuide />} />
            
            <Route path="/receipts" element={
              <ProtectedRoute user={user}>
                <Receipts userRole={userRole} />
              </ProtectedRoute>
            } />
            
            <Route path="/receipt/:id" element={
              <ProtectedRoute user={user}>
                <ViewReceipt userRole={userRole} />
              </ProtectedRoute>
            } />
            
            <Route path="/create-receipt" element={
              <ProtectedRoute user={user}>
                <CreateReceipt />
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute user={user}>
                <Profile user={user} />
              </ProtectedRoute>
            } />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      )}
    </div>
  );
}

export default App;
